import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, CalendarClock, Info, RefreshCw, X } from 'lucide-react';
import { Kanban } from '../../componentes/kanban';
import { SeloVerticais, type EmpresaCatalogo } from '../../componentes/verticais';
import { data, dataHora, dinheiro, dinheiroPorMoeda, nomeParceiro, nomePessoa, percentual, textoLivre } from '../../nucleo/formato';
import { Estrelas, iniciais } from './CrmComponentes';
import type { Estatistica } from './crm-filtros';
import { iconeDoTipo } from './icones-atividade';
import { rotuloRegime } from './crm-regime';

/**
 * CRM no novo desenho, lote 3 (C-A): o funil do Comercial (o do clone do RD). Uma coluna por etapa com o total e o valor do
 * filtro, as estatísticas da etapa (conversão, em andamento, sem tarefas, com atrasadas, sem produtos; clicar filtra a coluna) e
 * os cartões com os campos escolhidos em "Personalizar cartões". Arrastar move a negociação entre etapas abertas.
 */

export interface NegociacaoCrm {
  id: string;
  titulo: string;
  parceiro_id: string;
  parceiro_nome: string | null;
  /** rcrm4 SPEC-B2: a negociação só com contato tem a pessoa como parceiro. */
  parceiro_tipo?: 'empresa' | 'pessoa' | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  qualificacao: number;
  funil_id: string;
  /** Nome do funil (a lista do servidor já traz). */
  funil_nome?: string | null;
  etapa_id: string;
  etapa_nome: string;
  valor_centavos: number;
  valor_unico_centavos: number;
  valor_recorrente_centavos: number;
  itens: number;
  moeda: string;
  status: 'andamento' | 'vendido' | 'perdido' | 'pausado';
  utm_fonte: string | null;
  utm_campanha: string | null;
  previsao_fechamento: string | null;
  created_at: string;
  fechada_em: string | null;
  ultimo_contato: string | null;
  proxima_tarefa: {
    id: string;
    titulo: string;
    tipo: string;
    data_hora: string;
  } | null;
  atividades_pendentes: number;
  atividades_atrasadas: number;
  /** Verticais da negociação (vazia = todas). */
  empresas?: string[];
  /** Lote crm5 (E10): regime tributário pretendido. */
  regime_pretendido?: string | null;
}
export interface EtapaCrm {
  id: string;
  nome: string;
  tipo: 'aberta' | 'ganha' | 'perdida';
  ordem: number;
  total: number;
  valor_centavos: number;
  /** Lista de 04/11, item 3: o valor da etapa por moeda. */
  valores: { moeda: string; valor: number }[];
  negociacoes: NegociacaoCrm[];
}
export interface EstatisticaEtapa {
  etapa_id: string;
  andamento: number;
  sem_tarefa: number;
  atrasadas: number;
  sem_produtos: number;
  conversao: { entraram: number; avancaram: number; taxa: number | null };
}

const COR_STATUS: Record<NegociacaoCrm['status'], string> = {
  andamento: 'bg-azul',
  vendido: 'bg-verde',
  perdido: 'bg-perigo',
  pausado: 'bg-tinta-3',
};

/**
 * Lote crm5 (E6, decisão B): avanço no pipeline pela posição da etapa entre as etapas abertas do funil (pela ordem): a etapa i
 * de n vale round(i / n * 100)% (4 etapas: 25, 50, 75 e 100). Etapa que não é aberta (ganha ou perdida) não tem avanço.
 */
export function avancoNoPipeline(etapas: readonly Pick<EtapaCrm, 'id' | 'tipo' | 'ordem'>[], etapaId: string): number | null {
  const abertas = etapas.filter((e) => e.tipo === 'aberta').sort((a, b) => a.ordem - b.ordem);
  const i = abertas.findIndex((e) => e.id === etapaId);
  if (i < 0) return null;
  return Math.round(((i + 1) / abertas.length) * 100);
}

export function CartaoNegociacao({ n, campos, catalogo = [] }: { n: NegociacaoCrm; campos: string[]; catalogo?: readonly EmpresaCatalogo[] }) {
  const { t } = useTranslation('pipeline');
  const tem = (c: string) => campos.includes(c);
  const valor = n.itens ? n.valor_unico_centavos + n.valor_recorrente_centavos : n.valor_centavos;
  const IconeTarefa = n.proxima_tarefa ? iconeDoTipo(n.proxima_tarefa.tipo) : CalendarClock;
  return (
    <div className="flex flex-col gap-1" data-cartao-negociacao={n.id}>
      {/* pr-7: o canto de cima fica para o botão "Sobre a negociação" (fora do cartão arrastável). */}
      <p className="flex items-center gap-1.5 pr-7 text-xs text-tinta-2">
        <span className={`size-2 shrink-0 rounded-full ${COR_STATUS[n.status]}`} aria-hidden />
        {t(`crm.comercial.statusNeg.${n.status}`)}
      </p>
      <p className="font-semibold break-words">{textoLivre(n.titulo, n.parceiro_nome)}</p>
      {tem('empresa') && n.parceiro_nome && <p className="text-xs text-tinta-2">{nomeParceiro(n.parceiro_nome)}</p>}
      {tem('vertical') && catalogo.length > 1 && (
        <p className="text-xs" data-vertical-cartao="">
          <SeloVerticais empresas={n.empresas ?? []} catalogo={catalogo} />
        </p>
      )}
      {tem('proxima_tarefa') && (
        <p className={`flex items-center gap-1 text-xs ${n.atividades_atrasadas ? 'text-laranja' : 'text-tinta-3'}`} data-tipo-tarefa={n.proxima_tarefa?.tipo}>
          <IconeTarefa className="size-3.5 shrink-0" aria-hidden />
          {n.proxima_tarefa ? `${textoLivre(n.proxima_tarefa.titulo, n.parceiro_nome)} · ${dataHora(n.proxima_tarefa.data_hora)}` : t('crm.comercial.semTarefas')}
        </p>
      )}
      {tem('criada_em') && <p className="text-xs text-tinta-3">{t('crm.comercial.criadaEm', { data: data(n.created_at) })}</p>}
      {tem('fonte') && n.utm_fonte && <p className="text-xs text-tinta-3">{t('crm.comercial.fonteCartao', { fonte: n.utm_fonte })}</p>}
      {tem('regime') && n.regime_pretendido && (
        <p className="text-xs text-tinta-3" data-regime-cartao={n.regime_pretendido}>
          {t('crm.comercial.regimeCartao', { regime: rotuloRegime(n.regime_pretendido) })}
        </p>
      )}
      {tem('previsao') && n.previsao_fechamento && (
        <p className="text-xs text-tinta-3">
          {t('crm.comercial.previsaoCartao', {
            data: data(`${n.previsao_fechamento}T12:00`),
          })}
        </p>
      )}
      {(tem('qualificacao') || tem('responsavel') || tem('valor')) && (
        <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          {tem('qualificacao') && <Estrelas compacta valor={n.qualificacao} rotulo={t('crm.comercial.estrelas', { count: n.qualificacao })} />}
          {tem('responsavel') && n.responsavel_nome && (
            <span title={nomePessoa(n.responsavel_nome)} aria-label={nomePessoa(n.responsavel_nome)} className="flex size-6 items-center justify-center rounded-full bg-sup-3 text-xs font-semibold text-tinta-2">
              {iniciais(n.responsavel_nome)}
            </span>
          )}
          {tem('valor') && valor > 0 && <span className="numero ml-auto font-medium">{dinheiro(valor, n.moeda)}</span>}
        </p>
      )}
    </div>
  );
}

export function FunilComercial({
  etapas,
  estatisticas,
  campos,
  mostrar,
  aoMostrar,
  estatistica,
  aoFiltrarEstatistica,
  aoMover,
  aoAbrir,
  podeMover,
  aoRecarregar,
  aoReordenar,
  aoSobre,
  catalogo = [],
}: {
  etapas: EtapaCrm[];
  estatisticas: EstatisticaEtapa[];
  campos: string[];
  mostrar: Record<string, boolean>;
  aoMostrar: (etapaId: string, v: boolean) => void;
  estatistica: { etapa_id: string; chave: Estatistica } | null;
  aoFiltrarEstatistica: (v: { etapa_id: string; chave: Estatistica } | null) => void;
  aoMover: (n: NegociacaoCrm, etapaId: string) => void;
  aoAbrir: (n: NegociacaoCrm) => void;
  podeMover: boolean;
  /** K8: "Recarregar etapa" (busca o funil de novo). */
  aoRecarregar?: (etapa: EtapaCrm) => void;
  /** K11: a nova ordem das etapas (só com permissão de editar o funil). */
  aoReordenar?: (ids: string[]) => void;
  /** K18: "Sobre a negociação" (o ícone i do cartão). */
  aoSobre?: (n: NegociacaoCrm) => void;
  catalogo?: readonly EmpresaCatalogo[];
}) {
  const { t } = useTranslation('pipeline');
  const [chave] = useState(0);
  const itens = etapas.flatMap((e) => e.negociacoes);
  return (
    <Kanban<NegociacaoCrm>
      key={chave}
      itens={itens}
      colunaDe={(n) => n.etapa_id}
      rotulo={(n) => textoLivre(n.titulo, n.parceiro_nome)}
      aoClicar={aoAbrir}
      aoMover={(n, destino) => {
        if (podeMover) aoMover(n, destino);
      }}
      contagemNoTitulo
      naAlturaDaJanela
      aoReordenarColunas={aoReordenar}
      acoesDoCartao={
        aoSobre
          ? (n) => (
              <button
                type="button"
                data-sobre-negociacao={n.id}
                aria-label={t('crm.comercial.sobreDe', {
                  nome: textoLivre(n.titulo, n.parceiro_nome),
                })}
                title={t('crm.comercial.sobre')}
                onClick={() => aoSobre(n)}
                className="flex size-7 items-center justify-center rounded text-tinta-3 hover:bg-sup-3 hover:text-tinta-1"
              >
                <Info className="size-4" aria-hidden />
              </button>
            )
          : undefined
      }
      render={(n) => <CartaoNegociacao n={n} campos={campos} catalogo={catalogo} />}
      colunas={etapas.map((e) => {
        const est = estatisticas.find((x) => x.etapa_id === e.id);
        const aberta = !!mostrar[e.id];
        const linha = (k: Estatistica, n: number | undefined) => {
          const ativa = estatistica?.etapa_id === e.id && estatistica.chave === k;
          return (
            <button
              key={k}
              type="button"
              aria-pressed={ativa}
              data-estatistica={k}
              onClick={() => aoFiltrarEstatistica(ativa ? null : { etapa_id: e.id, chave: k })}
              className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs hover:bg-sup-3 aria-pressed:bg-sup-3 aria-pressed:font-semibold"
            >
              <span>{t(`crm.comercial.estatisticas.${k}`)}</span>
              <span className="numero">{n ?? 0}</span>
            </button>
          );
        };
        return {
          id: e.id,
          titulo: e.nome,
          total: e.total,
          // Lista de 04/11, item 3: uma linha por moeda, nunca somando.
          resumo: dinheiroPorMoeda(e.valores),
          acoes: (
            <>
              {aoRecarregar && (
                <button
                  type="button"
                  aria-label={t('crm.comercial.recarregarEtapaDe', {
                    etapa: e.nome,
                  })}
                  title={t('crm.comercial.recarregarEtapa')}
                  data-recarregar-etapa={e.id}
                  onClick={() => aoRecarregar(e)}
                  className="rounded p-1 text-tinta-3 hover:bg-sup-3 hover:text-tinta-1"
                >
                  <RefreshCw className="size-4" aria-hidden />
                </button>
              )}
              <button
                type="button"
                aria-pressed={aberta}
                aria-label={t(aberta ? 'crm.comercial.esconderEstatisticas' : 'crm.comercial.mostrarEstatisticas', { etapa: e.nome })}
                title={t(aberta ? 'crm.comercial.esconderEstatisticas' : 'crm.comercial.mostrarEstatisticas', { etapa: e.nome })}
                data-mostrar-estatisticas={e.id}
                onClick={() => aoMostrar(e.id, !aberta)}
                className="rounded p-1 text-tinta-3 hover:bg-sup-3 hover:text-tinta-1"
              >
                {aberta ? <X className="size-4" aria-hidden /> : <BarChart3 className="size-4" aria-hidden />}
              </button>
            </>
          ),
          topo:
            aberta && est ? (
              <div className="flex flex-col gap-0.5 border-b border-borda bg-sup-2 px-2 py-2" data-estatisticas-etapa={e.id}>
                {/* K6: a conversão em destaque (fundo ciano claro, percentual em negrito, "(x de y)" menor), como no clone. */}
                {e.tipo === 'aberta' && (
                  <p
                    className="mb-1 flex items-center justify-between gap-2 rounded-md bg-ciano/8 px-2 py-1.5 text-sm"
                    data-conversao-destaque=""
                    title={t('crm.comercial.conversaoAjuda', {
                      entraram: est.conversao.entraram,
                      avancaram: est.conversao.avancaram,
                    })}
                  >
                    <span className="font-semibold">{t('crm.comercial.conversao')}</span>
                    <span className="numero font-bold" data-conversao="">
                      {est.conversao.taxa === null ? '...' : `${percentual(est.conversao.taxa)}%`}{' '}
                      <span className="text-xs font-normal text-tinta-3">
                        {t('crm.comercial.conversaoDe', {
                          avancaram: est.conversao.avancaram,
                          entraram: est.conversao.entraram,
                        })}
                      </span>
                    </span>
                  </p>
                )}
                {linha('andamento', est.andamento)}
                {linha('sem_tarefa', est.sem_tarefa)}
                {linha('atrasadas', est.atrasadas)}
                {linha('sem_produtos', est.sem_produtos)}
                {/* E6 (decisão B): o avanço no pipeline pela posição da etapa entre as abertas (só nas etapas abertas). */}
                {e.tipo === 'aberta' && (
                  <p className="mt-1 flex items-center justify-between gap-2 border-t border-borda px-2 pt-1.5 text-xs" data-avanco-pipeline="" title={t('crm.comercial.avancoAjuda')}>
                    <span>{t('crm.comercial.avanco')}</span>
                    <span className="numero font-semibold">{(() => {
                      const pct = avancoNoPipeline(etapas, e.id);
                      return pct === null ? '...' : `${percentual(pct)}%`;
                    })()}</span>
                  </p>
                )}
              </div>
            ) : undefined,
          rodape:
            e.total > e.negociacoes.length
              ? t('crm.comercial.mostrandoDe', {
                  n: e.negociacoes.length,
                  total: e.total,
                })
              : undefined,
        };
      })}
    />
  );
}
